import { Card, CardBody, CardHeader, Col, Form, FormGroup, Row } from 'react-bootstrap';
import React, { ChangeEvent } from 'react';
import ISupervisor from 'interfaces/ISupervisor';
import { EmdrDatePicker } from '../../../DatePicker/EmdrDatePicker';

interface ISupervisorEntry {
    supervisor: ISupervisor;
    index: number;
    isRenewal?: boolean;
    onInputChangeEvent: (name: string, value: string) => void;
    onCheckboxClick: (e: ChangeEvent<HTMLInputElement>) => void;
    validated: boolean;
}

const SupervisorEntry: React.FC<ISupervisorEntry> = ({
    supervisor,
    index,
    isRenewal,
    onInputChangeEvent,
    onCheckboxClick,
    validated,
}) => {
    const isValidDate = (dateStr?: string): boolean => {
        if (!dateStr) return false;
        try {
            return !isNaN(Date.parse(dateStr));
        } catch {
            return false;
        }
    };

    const isInvalidDate = (dateStr?: string): boolean => {
        if (!dateStr) return validated; // Empty is invalid when validation is required
        try {
            return isNaN(Date.parse(dateStr));
        } catch {
            return true;
        }
    };

    const formatDateForInput = (input?: string | Date): string => {
        if (!input || input === '') return '';
        try {
            return new Date(input).toISOString().split('T')[0];
        } catch {
            return '';
        }
    };

    const today = formatDateForInput(new Date());
    const endDate = formatDateForInput(supervisor.ongoing ? '' : (supervisor?.endDate ?? ''));

    return (
        <Card className="bg-white border-0 p-0">
            <CardHeader className="border-0 bg-transparent">
                <Card.Title className="fw-bold">{supervisor.name}</Card.Title>
                <Card.Subtitle className="text-black-50">
                    <div>{supervisor.email}</div>
                    {!isRenewal && (
                        <div>
                            clients ({supervisor.numberOfClients}), sessions (
                            {supervisor.numberOfSessions})
                        </div>
                    )}
                </Card.Subtitle>
            </CardHeader>
            <CardBody className="text-black">
                <Row>
                    <Col xs={10}>
                        {/* Start Date Input */}
                        <FormGroup
                            key={`supervisor[${index}].startDate`}
                            controlId={`supervisor[${index}].startDate`}
                        >
                            <Row>
                                <Form.Label className="fw-bold">
                                    When did {supervisor.name} start supervising you?
                                </Form.Label>
                            </Row>
                            <Row className="mb-3">
                                <Col xs={10} md={7} lg={7} xl={6} xxl={5}>
                                    <EmdrDatePicker
                                        id={`supervisor[${index}].startDate`}
                                        name="startDate"
                                        value={supervisor?.startDate || ''}
                                        max={today}
                                        required
                                        showValidation={validated}
                                        onChange={onInputChangeEvent}
                                    />
                                </Col>
                            </Row>
                        </FormGroup>

                        {/* End Date Input */}

                        <Row>
                            <Form.Label
                                className="fw-bold"
                                htmlFor={`supervisor[${index}].endDate`}
                            >
                                When did this supervision end?
                            </Form.Label>
                        </Row>
                        <Row className="mb-3 align-items-baseline">
                            <Col xs={10} md={7} lg={7} xl={6} xxl={5}>
                                <FormGroup key={`supervisor[${index}].endDate`}>
                                    <EmdrDatePicker
                                        id={`supervisor[${index}].endDate`}
                                        name="endDate"
                                        value={endDate}
                                        max={today}
                                        required={!supervisor.ongoing}
                                        readOnly={supervisor.ongoing}
                                        showValidation={validated && !supervisor.ongoing}
                                        onChange={onInputChangeEvent}
                                    />
                                </FormGroup>
                            </Col>
                            <Col>
                                <FormGroup
                                    key={`supervisor[${index}].ongoing`}
                                    controlId={`supervisor[${index}].ongoing`}
                                >
                                    <Form.Label className="d-flex">
                                        <Form.Check
                                            className="mb-2 mx-2"
                                            name="ongoing"
                                            onChange={onCheckboxClick}
                                            checked={!!supervisor.ongoing}
                                            isValid={
                                                !!supervisor.ongoing ||
                                                isValidDate(supervisor?.endDate)
                                            }
                                            isInvalid={
                                                !supervisor.ongoing &&
                                                isInvalidDate(supervisor?.endDate)
                                            }
                                        />
                                        It's ongoing
                                    </Form.Label>
                                </FormGroup>
                            </Col>
                        </Row>
                    </Col>
                </Row>
                <Row className="me-auto float-end">
                    <FormGroup
                        key={`supervisor[${index}].isPrimary`}
                        controlId={`supervisor[${index}].isPrimary`}
                    >
                        <Form.Label className="fw-bold d-flex">
                            <Form.Check
                                className="mb-2 mx-2"
                                type="radio"
                                name="primary"
                                disabled={isRenewal}
                                onChange={onCheckboxClick}
                                checked={isRenewal || !!supervisor.primary}
                            />
                            This is my primary supervisor
                        </Form.Label>
                    </FormGroup>
                </Row>
            </CardBody>
        </Card>
    );
};

export default SupervisorEntry;
